// Libraries
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
// Assets
import { postNewInvoice } from '@/api/Sherpa/Draw';
import type { PostNewInvoicePayload } from '@/api/Sherpa/Draw/types';
import { getTdmsByProjectPhase } from '@/api/tdm';
import Button from '@/components/UI/Button';
// custom components
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import HookMaskedInput from '@/components/UI/HookForm/Form/MaskedInput';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { SherpaProjectReactQueryKey, TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import { type UploadTemplatesParam, useInvoiceFileUpload } from '@/hooks/useInvoiceFileUpload ';
import { notifyUser } from '@/store/Toast';
import type { Deliverable } from '@/types/Constants';
import { formatDate2, getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import { Phases } from './constant';
import { validationSchema } from './validationSchema';

interface NewDocumentProps {
  handleModal: () => void;
  projectName: string;
}

const drawDefaultValues = {
  phase: '',
  documentType: '',
  documentFile: '',
  name: '',
  summary: '',
  notes: '',
};

const NewBaseInvoices = ({ handleModal, projectName }: NewDocumentProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { projectId, drawId } = useParams();
  const { watch } = useFormContext();

  const invoiceFileRef = useRef<File[]>(null);
  const additionalDocumentsFileRef = useRef<File[]>(null);
  const signAndUploadFiles = useInvoiceFileUpload();

  const phase = watch('phase');

  //Tanstack: version 5 query implementation
  const fetchDeliverables = useQuery({
    queryKey: [TdmReactQueryKey.GET_PROJECT_DELIVERABLES, projectId, phase.value],
    queryFn: () =>
      getTdmsByProjectPhase({
        type: 'deliverables',
        phase: phase.value,
        page: 1,
        perPage: 10,
        projectId: Number(projectId),
      }),
  });
  const deliverables = fetchDeliverables?.data?.data;

  //Tanstack: version 5 query implementation
  const fetchMilestones = useQuery({
    queryKey: [TdmReactQueryKey.GET_PROJECT_MILESTONES, projectId, phase.value],
    queryFn: () =>
      getTdmsByProjectPhase({
        type: 'milestones',
        phase: phase.value,
        page: 1,
        perPage: 10,
        projectId: Number(projectId),
      }),
  });
  const milestones = fetchMilestones?.data?.data;

  useEffect(() => {
    if (signAndUploadFiles.uploadSuccess) {
      //Tanstack: version 5 query implementation
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_INVOICES] });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Project Invoice updated successfully',
          toastType: 'success',
        })
      );
      handleModal();
    }
  }, [signAndUploadFiles.uploadSuccess, handleModal, dispatch, queryClient]);

  const addInvoices = useMutation(
    (data: PostNewInvoicePayload) =>
      postNewInvoice({
        data,
        projectId: Number(projectId),
        drawId: Number(drawId),
      }),
    {
      onSuccess: (res) => {
        const templateFiles: UploadTemplatesParam = {};

        if (invoiceFileRef.current) {
          templateFiles.invoice = {
            templateFile: invoiceFileRef.current,
            drawId: Number(drawId),
            projectId: Number(projectId),
            invoiceId: Number(res.data.id),
          };
        }
        if (additionalDocumentsFileRef.current) {
          templateFiles.additionalDoc = {
            templateFile: additionalDocumentsFileRef.current,
            drawId: Number(drawId),
            projectId: Number(projectId),
            invoiceId: Number(res.data.id),
          };
        }

        signAndUploadFiles.uploadTemplates(templateFiles);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const submitNewInvoiceHandler = (data: any) => {
    const { invoiceDocuments, additionalDocuments } = data;
    const transformedData: PostNewInvoicePayload = {
      phaseSlug: data.phase?.value,
      amount: data?.amount,
      dueDate: formatDate2(data.dueDate),
      invoiceNumber: data.invoiceNumber,
    };

    if (data?.milestoneId.id) {
      transformedData.milestoneId = Number(data?.milestoneId.id);
    }

    if (data?.deliverableId.id) {
      transformedData.deliverableId = Number(data?.deliverableId.id);
    }

    if (data?.notes) {
      transformedData.notes = data.notes;
    }

    if (invoiceDocuments?.length) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      invoiceDocuments?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });
      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }
      if (validFiles.length > 0) {
        invoiceFileRef.current = validFiles;
      }
    }
    if (additionalDocuments?.length) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      additionalDocuments?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });
      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }
      if (validFiles.length > 0) {
        additionalDocumentsFileRef.current = validFiles;
      }
    }
    addInvoices.mutate(transformedData);
  };

  const minDate = () => {
    return dayjs().utc().startOf('day').format('YYYY-MM-DD');
  };

  return (
    <HookForm onSubmit={submitNewInvoiceHandler} className='space-y-2 grid grid-cols-2 gap-4'>
      <h3 className='text-sm col-span-2'>Project Name: {projectName}</h3>
      <HookReactSingleSelect componentClassName='col-span-1' label='Phase' name='phase' options={Phases} placeholder='' required />
      <HookInput name='invoiceNumber' type='text' label='Invoice Number*' />
      <HookMaskedInput name='amount' label='Invoice Amount*' maskedComponent='currency' />
      <HookInput name='dueDate' type='date' label='Due Date*' min={minDate()} />
      <div className='col-span-2'>
        <HookFileInput label='Invoice*' name='invoiceDocuments' id='invoiceDocuments' multiple />
      </div>

      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Deliverable'
        name='deliverableId'
        options={deliverables}
        getOptionLabel={(option) => (option as Deliverable).name}
        getOptionValue={(option) => (option as Deliverable).id.toString()}
        placeholder=''
      />
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Milestone'
        name='milestoneId'
        options={milestones}
        getOptionLabel={(option) => (option as Deliverable).name}
        getOptionValue={(option) => (option as Deliverable).id.toString()}
        placeholder=''
      />
      <div className='col-span-2'>
        <HookFileInput label='Additional Documents' name='additionalDocuments' id='additionalDocuments' multiple />
      </div>

      <div className='col-span-2'>
        <HookTextarea name='notes' label='Notes' rows={2} />
      </div>
      <div className='flex gap-4 mt-5 justify-end col-span-2'>
        <Button
          label='Cancel'
          type='button'
          btnStyle='btn-neutral'
          disabled={addInvoices.isLoading || signAndUploadFiles.isLoading}
          onClick={handleModal}
        />
        <Button
          label={`${addInvoices.isLoading || signAndUploadFiles.isLoading ? 'Creating...' : 'Add'}`}
          type='submit'
          btnStyle='btn-primary'
          disabled={addInvoices.isLoading || signAndUploadFiles.isLoading}
        />
      </div>
    </HookForm>
  );
};

export const NewInvoices = (props: NewDocumentProps) => {
  return (
    <Modal>
      <h2 className='mt-0'>New Invoice</h2>
      <HookFormProvider defaultValues={drawDefaultValues} resolver={yupResolver(validationSchema) as any}>
        <NewBaseInvoices {...props} />
      </HookFormProvider>
    </Modal>
  );
};
